import React, { useState, useEffect } from 'react';
import api from '../../services/api';

// Tipi di destinatario disponibili
const TIPI_DESTINATARIO = [
  {
    value: 'fornitore_scadenza',
    label: 'Fornitore della scadenza',
    description: 'Il fornitore associato direttamente alla scadenza',
    requiresSelection: false
  },
  {
    value: 'fornitori_tipologia',
    label: 'Fornitori per tipologia intervento',
    description: 'Tutti i fornitori abilitati per questa tipologia di intervento',
    requiresSelection: false
  },
  {
    value: 'fornitori_rsa',
    label: 'Fornitori attivi su RSA',
    description: 'Tutti i fornitori con contratti attivi sulla RSA',
    requiresSelection: false
  },
  {
    value: 'rsa_direzione',
    label: 'Direzione RSA',
    description: 'Email della direzione della struttura',
    requiresSelection: false
  },
  {
    value: 'rsa_amministrativo',
    label: 'Ufficio Amministrativo RSA',
    description: 'Email dell\'ufficio amministrativo della struttura',
    requiresSelection: false
  },
  {
    value: 'ruolo',
    label: 'Utenti per ruolo',
    description: 'Tutti gli utenti con un determinato ruolo',
    requiresSelection: true,
    selectionType: 'ruolo'
  },
  {
    value: 'custom',
    label: 'Email personalizzate',
    description: 'Lista di indirizzi email specifici',
    requiresSelection: true,
    selectionType: 'emails'
  }
];

// Ruoli disponibili per selezione
const RUOLI_DISPONIBILI = [
  { value: 'ADMIN', label: 'Amministratore' },
  { value: 'RSPP', label: 'RSPP' },
  { value: 'DIREZIONE', label: 'Direzione' },
  { value: 'RESPONSABILE_MANUTENZIONE', label: 'Responsabile Manutenzione' },
  { value: 'OPERATORE', label: 'Operatore' }
];

/**
 * Componente per costruire la configurazione dei destinatari email
 */
function DestinatariBuilder({
  value = { tipo: '', ruoli: [], emailCustom: [], cc: [] },
  onChange,
  showCC = true,
  contextType = 'scadenza', // scadenza, contratto, intervento
  className = ''
}) {
  const [emailInput, setEmailInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [validationError, setValidationError] = useState('');
  const [previewDestinatari, setPreviewDestinatari] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Filtra i tipi di destinatario in base al contesto
  const tipiDisponibili = TIPI_DESTINATARIO.filter(tipo => {
    if (contextType === 'manuale') return true;
    if (contextType === 'contratto' && tipo.value.includes('scadenza')) return false;
    return true;
  });

  // Aggiorna il tipo di destinatario
  const handleTipoChange = (nuovoTipo) => {
    const tipoInfo = TIPI_DESTINATARIO.find(t => t.value === nuovoTipo);
    onChange({
      ...value,
      tipo: nuovoTipo,
      ruoli: tipoInfo?.selectionType === 'ruolo' ? value.ruoli : [],
      emailCustom: tipoInfo?.selectionType === 'emails' ? value.emailCustom : []
    });
    setPreviewDestinatari(null);
  };

  // Toggle ruolo
  const toggleRuolo = (ruolo) => {
    const ruoli = value.ruoli || [];
    const nuoviRuoli = ruoli.includes(ruolo)
      ? ruoli.filter(r => r !== ruolo)
      : [...ruoli, ruolo];
    onChange({ ...value, ruoli: nuoviRuoli });
  };

  // Valida email
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email.trim());
  };

  // Aggiungi email custom
  const addCustomEmail = () => {
    const email = emailInput.trim();
    if (!email) return;

    if (!validateEmail(email)) {
      setValidationError('Inserisci un indirizzo email valido');
      return;
    }

    if (value.emailCustom?.includes(email)) {
      setValidationError('Email già presente nella lista');
      return;
    }

    onChange({
      ...value,
      emailCustom: [...(value.emailCustom || []), email]
    });
    setEmailInput('');
    setValidationError('');
  };

  // Rimuovi email custom
  const removeCustomEmail = (email) => {
    onChange({
      ...value,
      emailCustom: value.emailCustom.filter(e => e !== email)
    });
  };

  // Aggiungi email CC
  const addCCEmail = () => {
    const email = ccInput.trim();
    if (!email) return;

    if (!validateEmail(email)) {
      setValidationError('Inserisci un indirizzo email CC valido');
      return;
    }

    if (value.cc?.includes(email)) {
      setValidationError('Email CC già presente');
      return;
    }

    onChange({
      ...value,
      cc: [...(value.cc || []), email]
    });
    setCcInput('');
    setValidationError('');
  };

  // Rimuovi email CC
  const removeCCEmail = (email) => {
    onChange({
      ...value,
      cc: value.cc.filter(e => e !== email)
    });
  };

  // Toggle ruolo CC
  const toggleCCRuolo = (ruolo) => {
    const ccRuoli = value.ccRuoli || [];
    const nuoviCCRuoli = ccRuoli.includes(ruolo)
      ? ccRuoli.filter(r => r !== ruolo)
      : [...ccRuoli, ruolo];
    onChange({ ...value, ccRuoli: nuoviCCRuoli });
  };

  // Carica preview destinatari
  const loadPreview = async () => {
    if (!value.tipo) return;

    setLoadingPreview(true);
    try {
      const response = await api.post('/api/notifiche/destinatari/risolvi', {
        tipo: value.tipo,
        ruoli: value.ruoli,
        emailCustom: value.emailCustom,
        // Per il preview usiamo dati fittizi
        scadenzaId: 'preview',
        rsaId: 'preview'
      });
      setPreviewDestinatari(response.data.data);
    } catch (error) {
      console.error('Errore caricamento preview:', error);
      setPreviewDestinatari({ error: 'Impossibile caricare anteprima' });
    } finally {
      setLoadingPreview(false);
    }
  };

  // Gestione invio con Enter
  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomEmail();
    }
  };

  const handleCCKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCCEmail();
    }
  };

  const selectedTipo = TIPI_DESTINATARIO.find(t => t.value === value.tipo);

  return (
    <div className={`destinatari-builder ${className}`}>
      {/* Selezione tipo destinatario */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tipo Destinatario
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {tipiDisponibili.map(tipo => (
            <button
              key={tipo.value}
              type="button"
              onClick={() => handleTipoChange(tipo.value)}
              className={`text-left p-3 rounded-lg border-2 transition-all ${
                value.tipo === tipo.value
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="font-medium text-gray-900">{tipo.label}</div>
              <div className="text-xs text-gray-500 mt-1">{tipo.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Selezione ruoli (se tipo = ruolo) */}
      {value.tipo === 'ruolo' && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seleziona Ruoli
          </label>
          <div className="flex flex-wrap gap-2">
            {RUOLI_DISPONIBILI.map(ruolo => (
              <button
                key={ruolo.value}
                type="button"
                onClick={() => toggleRuolo(ruolo.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  value.ruoli?.includes(ruolo.value)
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-gray-700 border hover:bg-gray-100'
                }`}
              >
                {ruolo.label}
              </button>
            ))}
          </div>
          {value.ruoli?.length === 0 && (
            <p className="text-xs text-amber-600 mt-2">
              Seleziona almeno un ruolo
            </p>
          )}
        </div>
      )}

      {/* Input email custom (se tipo = custom) */}
      {value.tipo === 'custom' && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Indirizzi Email
          </label>

          {/* Lista email aggiunte */}
          {value.emailCustom?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {value.emailCustom.map(email => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-white border rounded-full text-sm"
                >
                  <span className="text-gray-700">{email}</span>
                  <button
                    type="button"
                    onClick={() => removeCustomEmail(email)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Input nuova email */}
          <div className="flex gap-2">
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleEmailKeyDown}
              placeholder="nome@esempio.com"
              className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              type="button"
              onClick={addCustomEmail}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
            >
              Aggiungi
            </button>
          </div>

          {validationError && (
            <p className="text-xs text-red-500 mt-2">{validationError}</p>
          )}
        </div>
      )}

      {/* Sezione CC (opzionale) */}
      {showCC && value.tipo && (
        <div className="mb-4 p-4 border border-dashed border-gray-300 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Copia Conoscenza (CC) - Opzionale
          </label>

          {/* CC per ruolo */}
          <div className="mb-3">
            <span className="text-xs text-gray-500 mb-2 block">Aggiungi ruoli in CC:</span>
            <div className="flex flex-wrap gap-2">
              {RUOLI_DISPONIBILI.map(ruolo => (
                <button
                  key={ruolo.value}
                  type="button"
                  onClick={() => toggleCCRuolo(ruolo.value)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    value.ccRuoli?.includes(ruolo.value)
                      ? 'bg-gray-700 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {ruolo.label}
                </button>
              ))}
            </div>
          </div>

          {/* CC email specifiche */}
          <div>
            <span className="text-xs text-gray-500 mb-2 block">Oppure aggiungi email specifiche:</span>

            {value.cc?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {value.cc.map(email => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeCCEmail(email)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="email"
                value={ccInput}
                onChange={(e) => setCcInput(e.target.value)}
                onKeyDown={handleCCKeyDown}
                placeholder="cc@esempio.com"
                className="flex-1 px-2 py-1.5 border rounded text-xs focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={addCCEmail}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
              >
                Aggiungi CC
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Riepilogo configurazione */}
      {value.tipo && (
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="flex items-start justify-between">
            <div>
              <span className="text-sm font-medium text-blue-900">Configurazione:</span>
              <div className="text-sm text-blue-700 mt-1">
                <span className="font-medium">{selectedTipo?.label}</span>
                {value.tipo === 'ruolo' && value.ruoli?.length > 0 && (
                  <span> ({value.ruoli.join(', ')})</span>
                )}
                {value.tipo === 'custom' && value.emailCustom?.length > 0 && (
                  <span> ({value.emailCustom.length} email)</span>
                )}
                {(value.cc?.length > 0 || value.ccRuoli?.length > 0) && (
                  <span className="text-blue-600">
                    {' '}+ CC: {[...(value.cc || []), ...(value.ccRuoli || [])].length} destinatari
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={loadPreview}
              disabled={loadingPreview}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              {loadingPreview ? 'Caricamento...' : 'Anteprima destinatari'}
            </button>
          </div>

          {/* Preview destinatari */}
          {previewDestinatari && !previewDestinatari.error && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <span className="text-xs font-medium text-blue-800">
                Destinatari trovati: {previewDestinatari.length || 0}
              </span>
              {previewDestinatari.length > 0 && (
                <div className="mt-1 text-xs text-blue-600 max-h-20 overflow-y-auto">
                  {previewDestinatari.slice(0, 5).map((d, i) => (
                    <div key={i}>{d.nome || d.email} &lt;{d.email}&gt;</div>
                  ))}
                  {previewDestinatari.length > 5 && (
                    <div className="text-blue-500">...e altri {previewDestinatari.length - 5}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DestinatariBuilder;
export { TIPI_DESTINATARIO, RUOLI_DISPONIBILI };
